import * as React from 'react';
import { Download, FileSpreadsheet, Package, Printer, RotateCcw, Search, Warehouse } from 'lucide-react';
import { useContexto } from '@/hooks/useContexto';
import { useParamUrl, useAtualizarParams } from '@/hooks/useUrlEstado';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useRelatorioEstoque,
  useRelatorioLocalizadores,
} from '@/hooks/relatorios';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { imprimirEtiquetas } from '@/lib/imprimirEtiquetas';
import { ESTILO_STATUS } from '@/lib/status';
import { cn, dataCurta, numero, percentual } from '@/lib/utils';
import type { LinhaEstoqueAtual, LinhaLocalizador, SituacaoSaldo } from '@/lib/tipos';
import { DrawerPosicao } from '@/componentes/DrawerPosicao';
import { EtiquetaLocalizador } from '@/componentes/etiqueta/EtiquetaLocalizador';
import { GradeRelatorio, type ColunaGrade } from '@/componentes/tabela/GradeRelatorio';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  Badge,
  CabecalhoPagina,
  CampoComIcone,
  Esqueleto,
  Pilula,
} from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';

/**
 * Relatórios — duas perguntas diferentes, duas fontes diferentes.
 *
 *  CADASTRO DE LOCALIZADORES: grão = a posição, inclui as VAZIAS. Responde
 *  "quanto do CD está ocupado, quanto está livre, e onde".
 *
 *  ESTOQUE ATUAL: grão = produto × posição, direto da view do PBS. Responde
 *  "onde está o SKU X", "o que exatamente tem na posição Y", "o que está
 *  negativo e onde".
 *
 *  Clicar em qualquer linha dos dois abre a gaveta da posição, à direita, com
 *  os produtos e as quantidades — sem sair do relatório e sem perder o filtro.
 */
export function Relatorios(): JSX.Element {
  const { centroAtual } = useContexto();
  const [rel, definirRel] = useParamUrl('r', 'localizadores');
  const emEstoque = rel === 'estoque';

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<FileSpreadsheet className="h-5 w-5" />}
        sobrelinha={
          centroAtual ? `${centroAtual.centroEstoque} · ${centroAtual.nome}` : 'Centro'
        }
        titulo="Relatórios"
        descricao="Consulta livre sobre o CD, com filtros combináveis e exportação para Excel. Clique em qualquer linha para ver os produtos da posição."
        acoes={
          <Segmentado
            valor={emEstoque ? 'estoque' : 'localizadores'}
            aoMudar={(v) => definirRel(v)}
            rotuloAcessivel="Relatório"
            opcoes={[
              {
                valor: 'localizadores',
                rotulo: 'Localizadores',
                icone: <Warehouse className="h-4 w-4" />,
              },
              { valor: 'estoque', rotulo: 'Estoque atual', icone: <Package className="h-4 w-4" /> },
            ]}
          />
        }
      />

      {emEstoque ? <RelatorioEstoque /> : <RelatorioLocalizadores />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Cadastro de localizadores
// ---------------------------------------------------------------------------

const CSV_LOCALIZADORES: ColunaCsv<LinhaLocalizador>[] = [
  { cabecalho: 'Localizador', valor: (l) => l.localizador, texto: true },
  { cabecalho: 'Endereco', valor: (l) => l.endereco },
  { cabecalho: 'DescricaoCadastro', valor: (l) => l.descricaoCadastro },
  { cabecalho: 'Corredor', valor: (l) => l.corredor },
  { cabecalho: 'Modulo', valor: (l) => l.modulo },
  { cabecalho: 'Box', valor: (l) => l.box },
  { cabecalho: 'Nivel', valor: (l) => l.nivel },
  { cabecalho: 'Zona', valor: (l) => l.zona },
  { cabecalho: 'Ativo', valor: (l) => (l.ativo ? 'S' : 'N') },
  { cabecalho: 'Status', valor: (l) => ESTILO_STATUS[l.status].rotulo },
  { cabecalho: 'SKUs', valor: (l) => l.skus },
  { cabecalho: 'Saldo', valor: (l) => l.saldo },
  { cabecalho: 'PercentualOcupado', valor: (l) => l.pctOcupado ?? '' },
  { cabecalho: 'PercentualLivre', valor: (l) => l.pctLivre ?? '' },
];

function RelatorioLocalizadores(): JSX.Element {
  const { centro } = useContexto();
  const atualizar = useAtualizarParams();
  const [corredor] = useParamUrl('rua');
  const [modulo] = useParamUrl('mod');
  const [status] = useParamUrl('st');
  const [busca, definirBusca] = React.useState('');
  const buscaAtrasada = useDebounce(busca, 300);
  const [aberta, definirAberta] = React.useState<number | null>(null);
  /*
   * Etiqueta 100 × 50 do localizador (a do RTM do PBS): de uma linha, pelo
   * ícone, ou de todas as do filtro. Monta fora da tela e imprime pelo mesmo
   * caminho da etiqueta de separação (imprimirEtiquetas).
   */
  const [imprimindo, definirImprimindo] = React.useState<LinhaLocalizador[] | null>(null);
  const papelEtiquetas = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!imprimindo?.length || !papelEtiquetas.current) return;
    imprimirEtiquetas(papelEtiquetas.current, {
      titulo: imprimindo.length === 1 ? `Etiqueta localizador ${imprimindo[0]!.localizador}` : `Etiquetas localizadores (${imprimindo.length})`,
    });
    definirImprimindo(null);
  }, [imprimindo]);
  const imprimirFiltro = (): void => {
    const linhas = dados?.linhas ?? [];
    if (linhas.length > 50 && !window.confirm(`Imprimir ${linhas.length} etiquetas, uma por localizador do filtro?`)) return;
    definirImprimindo([...linhas]);
  };

  const consulta = useRelatorioLocalizadores(centro, {
    corredor,
    modulo,
    status,
    q: buscaAtrasada,
  });
  const dados = consulta.data;

  const colunas: ColunaGrade<LinhaLocalizador>[] = [
    {
      chave: 'etiqueta',
      cabecalho: '',
      largura: 44,
      render: (l) => (
        <button
          type="button"
          title="Imprimir a etiqueta deste localizador (100 × 50)"
          aria-label={`Imprimir etiqueta do localizador ${l.localizador}`}
          className="rounded-md p-1 text-textoFraco transition hover:bg-superficie2 hover:text-texto"
          onClick={(e) => {
            e.stopPropagation(); // o clique na linha abre a gaveta; aqui só imprime
            definirImprimindo([l]);
          }}
        >
          <Printer className="h-4 w-4" />
        </button>
      ),
    },
    {
      chave: 'localizador',
      cabecalho: 'Localizador',
      largura: 118,
      ordenar: (l) => l.localizador,
      render: (l) => <span className="tabular font-mono font-semibold">{l.localizador}</span>,
    },
    {
      chave: 'endereco',
      cabecalho: 'Endereço',
      largura: 180,
      elastica: true,
      ordenar: (l) => l.endereco,
      render: (l) => (
        <span className="font-mono">
          {l.endereco}
          {l.descricaoCadastro && l.descricaoCadastro !== l.endereco ? (
            <span className="ml-2 text-textoSuave">{l.descricaoCadastro}</span>
          ) : null}
        </span>
      ),
    },
    {
      chave: 'corredor',
      cabecalho: 'Rua',
      largura: 92,
      ordenar: (l) => l.corredor,
      render: (l) => <span className="font-mono">{l.corredor}</span>,
    },
    {
      chave: 'modulo',
      cabecalho: 'Módulo',
      largura: 88,
      ordenar: (l) => l.moduloOrdem,
      render: (l) => <span className="tabular">{l.modulo}</span>,
    },
    {
      chave: 'box',
      cabecalho: 'Box',
      largura: 74,
      ordenar: (l) => l.box,
      render: (l) => <span className="tabular">{l.box}</span>,
    },
    {
      chave: 'status',
      cabecalho: 'Status',
      largura: 132,
      ordenar: (l) => l.status,
      render: (l) => (
        <Badge className={ESTILO_STATUS[l.status].classeBadge}>
          <span aria-hidden>{ESTILO_STATUS[l.status].glifo}</span>
          {ESTILO_STATUS[l.status].rotulo}
        </Badge>
      ),
    },
    {
      chave: 'skus',
      cabecalho: 'SKUs',
      largura: 72,
      alinhar: 'dir',
      ordenar: (l) => l.skus,
      render: (l) => numero(l.skus),
    },
    {
      chave: 'saldo',
      cabecalho: 'Saldo',
      largura: 88,
      alinhar: 'dir',
      ordenar: (l) => l.saldo,
      render: (l) => (
        <span className={cn(l.saldo < 0 && 'font-semibold text-status-divergencia')}>
          {numero(l.saldo)}
        </span>
      ),
    },
    {
      chave: 'ocupado',
      cabecalho: '% ocupado',
      largura: 132,
      ordenar: (l) => l.pctOcupado ?? -1,
      render: (l) => <BarraPct pct={l.pctOcupado} />,
    },
    {
      chave: 'livre',
      cabecalho: '% livre',
      largura: 86,
      alinhar: 'dir',
      ordenar: (l) => l.pctLivre ?? -1,
      render: (l) =>
        l.pctLivre === null ? <span className="text-textoSuave">—</span> : `${l.pctLivre}%`,
    },
  ];

  const resumo = dados?.resumo;

  /** A linha aberta, no formato `Posicao` que a gaveta já sabe exibir. */
  const linhaSelecionada = React.useMemo(() => {
    const l = dados?.linhas.find((x) => x.localizador === aberta);
    if (!l) return undefined;
    return {
      localizador: l.localizador,
      descricao: l.endereco,
      empresa: 0,
      modulo: l.modulo,
      corredor: l.corredor,
      box: l.box,
      nivel: l.nivel,
      zona: l.zona,
      ativo: l.ativo,
      status: l.status,
      skus: l.skus,
      saldo: l.saldo,
      nivelRack: l.nivelRack,
      moduloOrdem: l.moduloOrdem,
      posicaoNoNivel: 1,
      geometriaConfiavel: true,
    };
  }, [dados, aberta]);

  return (
    <>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <CampoComIcone
          icone={<Search className="h-4 w-4" />}
          value={busca}
          onChange={(e) => definirBusca(e.target.value)}
          placeholder="Endereço ou código do localizador…"
          aria-label="Buscar localizador"
          className="w-[280px]"
          classeCampo="min-h-[38px] text-[13px]"
        />
        <SeletorFiltro
          rotulo="Rua"
          valor={corredor}
          opcoes={dados?.corredores ?? []}
          aoMudar={(v) => atualizar({ rua: v || null, mod: null })}
        />
        <SeletorFiltro
          rotulo="Módulo"
          valor={modulo}
          opcoes={dados?.modulos ?? []}
          aoMudar={(v) => atualizar({ mod: v || null })}
        />
        <SeletorFiltro
          rotulo="Status"
          valor={status}
          opcoes={['ocupada', 'livre', 'divergencia', 'indisponivel']}
          rotuloDe={(v) => ESTILO_STATUS[v as keyof typeof ESTILO_STATUS]?.rotulo ?? v}
          aoMudar={(v) => atualizar({ st: v || null })}
        />
        {corredor || modulo || status || busca ? (
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => {
              definirBusca('');
              atualizar({ rua: null, mod: null, st: null });
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Limpar
          </Botao>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          {dados?.dataBase ? (
            <Pilula tom="neutro">posições até {dataCurta(dados.dataBase)}</Pilula>
          ) : null}
          <Botao
            variante="secundaria"
            tamanho="pequeno"
            disabled={!dados || dados.linhas.length === 0}
            title="Uma etiqueta 100 × 50 por localizador do filtro atual"
            onClick={imprimirFiltro}
          >
            <Printer className="h-4 w-4" />
            Etiquetas{dados ? ` (${numero(dados.linhas.length)})` : ''}
          </Botao>
          <Botao
            variante="primaria"
            tamanho="pequeno"
            disabled={!dados || dados.linhas.length === 0}
            onClick={() =>
              baixarCsv(
                `localizadores-${centro ?? 'centro'}-${carimboArquivo()}`,
                dados?.linhas ?? [],
                CSV_LOCALIZADORES,
              )
            }
          >
            <Download className="h-4 w-4" />
            Exportar Excel
          </Botao>
        </div>
      </div>

      {/* Indicadores */}
      {resumo ? (
        <div className="grid gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
          <Indicador rotulo="Posições" valor={numero(resumo.posicoes)} nota="no filtro atual" />
          <Indicador
            rotulo="Ocupadas"
            valor={percentual(resumo.pctPosicoesOcupadas)}
            nota={`${numero(resumo.ocupadas)} posições`}
            tom="marca"
            barra={resumo.pctPosicoesOcupadas}
          />
          <Indicador
            rotulo="Livres"
            valor={percentual(resumo.pctPosicoesLivres)}
            nota={`${numero(resumo.livres)} posições`}
            tom="ok"
            barra={resumo.pctPosicoesLivres}
          />
          <Indicador
            rotulo="Ocupação volumétrica"
            valor={`${resumo.ocupacaoVolumetricaMediaPct}%`}
            nota={
              resumo.semMedida > 0
                ? `${numero(resumo.semMedida)} sem medida`
                : 'média das ocupadas'
            }
          />
          <Indicador
            rotulo="Divergências"
            valor={numero(resumo.divergencias)}
            nota="saldo negativo"
            tom={resumo.divergencias > 0 ? 'erro' : 'neutro'}
          />
          <Indicador
            rotulo="SKUs distintos"
            valor={numero(resumo.skusDistintos)}
            nota={`${numero(resumo.saldoTotal)} un no total`}
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1">
        {consulta.isLoading ? (
          <Esqueleto className="h-full min-h-[360px] w-full rounded-2xl" />
        ) : consulta.isError ? (
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        ) : (dados?.linhas.length ?? 0) === 0 ? (
          <EstadoVazio
            titulo="Nenhum localizador com esses filtros"
            detalhe="Limpe um filtro acima para ampliar o resultado."
            icone={<Search className="h-7 w-7" />}
          />
        ) : (
          <GradeRelatorio
            linhas={dados?.linhas ?? []}
            colunas={colunas}
            chaveDe={(l) => String(l.localizador)}
            selecionada={aberta === null ? null : String(aberta)}
            aoClicar={(l) => definirAberta(l.localizador)}
            className="h-full"
          />
        )}
      </div>

      {/* A gaveta recebe a linha inteira: assim o título é o ENDEREÇO e não
          um "Posição 64518" genérico, e ela abre já com contexto. */}
      <DrawerPosicao
        localizador={aberta}
        posicao={linhaSelecionada}
        aoFechar={() => definirAberta(null)}
      />

      {imprimindo ? (
        <div ref={papelEtiquetas} className="pointer-events-none absolute left-[-10000px] top-0" aria-hidden>
          {imprimindo.map((l) => (
            <EtiquetaLocalizador
              key={l.localizador}
              dados={{ localizador: l.localizador, descricao: l.descricaoCadastro || l.endereco, tipo: l.tipo }}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// 2. Estoque atual (view do PBS)
// ---------------------------------------------------------------------------

const CSV_ESTOQUE: ColunaCsv<LinhaEstoqueAtual>[] = [
  { cabecalho: 'SKU', valor: (l) => l.produto, texto: true },
  { cabecalho: 'Descricao', valor: (l) => l.descricao },
  { cabecalho: 'EAN', valor: (l) => l.ean ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (l) => l.localizador, texto: true },
  { cabecalho: 'Endereco', valor: (l) => l.endereco },
  { cabecalho: 'Corredor', valor: (l) => l.corredor },
  { cabecalho: 'Modulo', valor: (l) => l.modulo },
  { cabecalho: 'Box', valor: (l) => l.box },
  { cabecalho: 'Zona', valor: (l) => l.zona },
  { cabecalho: 'Ativo', valor: (l) => (l.ativo ? 'S' : 'N') },
  { cabecalho: 'Saldo', valor: (l) => l.saldo },
  { cabecalho: 'SaldoLocalizador', valor: (l) => l.saldoLocalizador },
  { cabecalho: 'SKUsLocalizador', valor: (l) => l.skusLocalizador },
  { cabecalho: 'PesoTotalKg', valor: (l) => l.pesoTotalKg ?? '' },
  { cabecalho: 'VolumeTotalM3', valor: (l) => l.volumeTotalM3 ?? '' },
];

const SITUACOES: { valor: SituacaoSaldo; rotulo: string }[] = [
  { valor: 'positivo', rotulo: 'Com saldo' },
  { valor: 'negativo', rotulo: 'Negativos' },
  { valor: 'zerado', rotulo: 'Zerados' },
  { valor: 'todos', rotulo: 'Todos' },
];

function RelatorioEstoque(): JSX.Element {
  const { centro } = useContexto();
  const atualizar = useAtualizarParams();
  const [corredor] = useParamUrl('rua');
  const [situacaoUrl] = useParamUrl('sit', 'positivo');
  const [busca, definirBusca] = React.useState('');
  const buscaAtrasada = useDebounce(busca, 300);
  const [limite, definirLimite] = React.useState(5000);
  const [aberta, definirAberta] = React.useState<number | null>(null);

  const situacao = (SITUACOES.find((s) => s.valor === situacaoUrl)?.valor ??
    'positivo') as SituacaoSaldo;

  const consulta = useRelatorioEstoque(centro, {
    produto: '',
    corredor,
    modulo: '',
    situacao,
    q: buscaAtrasada,
    limite,
  });
  const dados = consulta.data;

  const pe = dados?.resumo;
  // Pé fixo da grade: o recorte INTEIRO (o resumo do servidor), mesmo quando o limite cortou linhas.
  const rodape = (texto: React.ReactNode): React.ReactNode => (pe ? <span className="tabular font-semibold">{texto}</span> : undefined);
  const colunas: ColunaGrade<LinhaEstoqueAtual>[] = [
    {
      chave: 'produto',
      cabecalho: 'SKU',
      largura: 92,
      ordenar: (l) => l.produto,
      render: (l) => <span className="tabular font-mono font-semibold">{l.produto}</span>,
      rodape: rodape(`${numero(pe?.skus ?? 0)} SKUs`),
    },
    {
      chave: 'descricao',
      cabecalho: 'Produto',
      largura: 260,
      elastica: true,
      ordenar: (l) => l.descricao,
      render: (l) => (
        <span title={l.descricao}>
          {l.descricao}
          {l.ean ? <span className="ml-2 text-textoSuave">EAN {l.ean}</span> : null}
        </span>
      ),
    },
    {
      chave: 'localizador',
      cabecalho: 'Localizador',
      largura: 116,
      ordenar: (l) => l.localizador,
      render: (l) => <span className="tabular font-mono text-textoFraco">{l.localizador}</span>,
      rodape: rodape(`${numero(pe?.localizadores ?? 0)} localiz.`),
    },
    {
      chave: 'endereco',
      cabecalho: 'Endereço',
      largura: 150,
      ordenar: (l) => l.endereco,
      render: (l) => <span className="font-mono">{l.endereco}</span>,
    },
    {
      chave: 'corredor',
      cabecalho: 'Rua',
      largura: 88,
      ordenar: (l) => l.corredor,
      render: (l) => <span className="font-mono">{l.corredor}</span>,
    },
    {
      chave: 'saldo',
      cabecalho: 'Saldo',
      largura: 96,
      alinhar: 'dir',
      ordenar: (l) => l.saldo,
      render: (l) => (
        <span className={cn(l.saldo < 0 && 'font-semibold text-status-divergencia')}>
          {numero(l.saldo)}
        </span>
      ),
      rodape: rodape(numero(pe?.saldoTotal ?? 0)),
    },
    {
      chave: 'saldoLocalizador',
      cabecalho: 'Saldo do localizador',
      largura: 132,
      alinhar: 'dir',
      ordenar: (l) => l.saldoLocalizador,
      render: (l) => (
        <span
          title={`Tudo o que está no localizador ${l.localizador}: ${l.skusLocalizador} SKU(s) com saldo`}
          className={cn('tabular', l.saldoLocalizador < 0 && 'font-semibold text-status-divergencia')}
        >
          {numero(l.saldoLocalizador)}
          {l.skusLocalizador > 1 ? <span className="ml-1 text-textoSuave">· {l.skusLocalizador} SKUs</span> : null}
        </span>
      ),
      rodape: rodape(numero(pe?.saldoLocalizadores ?? 0)),
    },
    {
      chave: 'peso',
      cabecalho: 'Peso (kg)',
      largura: 104,
      alinhar: 'dir',
      ordenar: (l) => l.pesoTotalKg ?? -1,
      render: (l) =>
        l.pesoTotalKg === null ? (
          <span className="text-textoSuave">—</span>
        ) : (
          l.pesoTotalKg.toLocaleString('pt-BR')
        ),
      rodape: rodape(pe?.pesoTotalKg == null ? '—' : pe.pesoTotalKg.toLocaleString('pt-BR')),
    },
  ];

  const resumo = dados?.resumo;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <CampoComIcone
          icone={<Search className="h-4 w-4" />}
          value={busca}
          onChange={(e) => definirBusca(e.target.value)}
          placeholder="SKU, descrição, EAN, endereço…"
          aria-label="Buscar no estoque"
          className="w-[300px]"
          classeCampo="min-h-[38px] text-[13px]"
        />
        <Segmentado
          valor={situacao}
          aoMudar={(v) => atualizar({ sit: v })}
          rotuloAcessivel="Situação do saldo"
          tamanho="pequeno"
          opcoes={SITUACOES.map((s) => ({ valor: s.valor, rotulo: s.rotulo }))}
        />
        <SeletorFiltro
          rotulo="Rua"
          valor={corredor}
          opcoes={dados?.corredores ?? []}
          aoMudar={(v) => atualizar({ rua: v || null })}
        />
        {corredor || busca || situacao !== 'positivo' ? (
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => {
              definirBusca('');
              atualizar({ rua: null, sit: null });
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Limpar
          </Botao>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          {dados?.truncado ? (
            <Pilula tom="alerta">
              mostrando {numero(dados.limite)} de {numero(dados.resumo.linhas)}
            </Pilula>
          ) : null}
          {dados?.truncado ? (
            <Botao variante="secundaria" tamanho="pequeno" onClick={() => definirLimite(50_000)}>
              Carregar tudo
            </Botao>
          ) : null}
          <Botao
            variante="primaria"
            tamanho="pequeno"
            disabled={!dados || dados.linhas.length === 0}
            onClick={() =>
              baixarCsv(
                `estoque-atual-${centro ?? 'centro'}-${carimboArquivo()}`,
                dados?.linhas ?? [],
                CSV_ESTOQUE,
              )
            }
          >
            <Download className="h-4 w-4" />
            Exportar Excel
          </Botao>
        </div>
      </div>

      {resumo ? (
        <div className="grid gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
          <Indicador rotulo="Linhas" valor={numero(resumo.linhas)} nota="produto × posição" />
          <Indicador rotulo="SKUs" valor={numero(resumo.skus)} nota="distintos" tom="marca" />
          <Indicador
            rotulo="Localizadores"
            valor={numero(resumo.localizadores)}
            nota="posições envolvidas"
          />
          <Indicador rotulo="Saldo total" valor={numero(resumo.saldoTotal)} nota="unidades" />
          <Indicador
            rotulo="Linhas negativas"
            valor={numero(resumo.linhasNegativas)}
            nota={`${numero(resumo.saldoNegativo)} un`}
            tom={resumo.linhasNegativas > 0 ? 'erro' : 'neutro'}
          />
          <Indicador
            rotulo="Peso total"
            valor={resumo.pesoTotalKg === null ? '—' : `${numero(resumo.pesoTotalKg)} kg`}
            nota={resumo.pesoTotalKg === null ? 'há SKU sem peso cadastrado' : 'soma do filtro'}
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1">
        {consulta.isLoading ? (
          <Esqueleto className="h-full min-h-[360px] w-full rounded-2xl" />
        ) : consulta.isError ? (
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        ) : (dados?.linhas.length ?? 0) === 0 ? (
          <EstadoVazio
            titulo="Nenhuma linha com esses filtros"
            detalhe="A fonte é a view ESTOQUE_ATUAL_LOCALIZADORES do PBS, no grão produto × posição."
            icone={<Search className="h-7 w-7" />}
          />
        ) : (
          <GradeRelatorio
            linhas={dados?.linhas ?? []}
            colunas={colunas}
            chaveDe={(l) => `${l.produto}:${l.localizador}`}
            selecionada={null}
            aoClicar={(l) => definirAberta(l.localizador)}
            className="h-full"
          />
        )}
      </div>

      <DrawerPosicao localizador={aberta} aoFechar={() => definirAberta(null)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------------

function SeletorFiltro({
  rotulo,
  valor,
  opcoes,
  rotuloDe,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  opcoes: readonly string[];
  rotuloDe?: ((v: string) => string) | undefined;
  aoMudar: (valor: string) => void;
}): JSX.Element {
  return (
    <label className="flex items-center gap-1.5">
      <span className="sr-only">{rotulo}</span>
      <select
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        className={cn(
          'min-h-[38px] rounded-lg border border-borda bg-superficie px-2.5 text-[13px] font-medium shadow-xs',
          'transition-colors hover:border-bordaForte focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/10',
          valor && 'border-primaria/40 bg-primaria-suave text-primaria',
        )}
      >
        <option value="">{rotulo}: todos</option>
        {opcoes.map((o) => (
          <option key={o} value={o}>
            {rotulo}: {rotuloDe ? rotuloDe(o) : o}
          </option>
        ))}
      </select>
    </label>
  );
}

function Indicador({
  rotulo,
  valor,
  nota,
  tom = 'neutro',
  barra,
}: {
  rotulo: string;
  valor: string;
  nota: string;
  tom?: 'neutro' | 'marca' | 'ok' | 'erro';
  barra?: number | undefined;
}): JSX.Element {
  const cor = {
    neutro: 'text-texto',
    marca: 'text-primaria',
    ok: 'text-status-livre',
    erro: 'text-status-divergencia',
  }[tom];
  const fundoBarra = {
    neutro: 'bg-bordaForte',
    marca: 'gradiente-marca',
    ok: 'bg-status-livre',
    erro: 'bg-status-divergencia',
  }[tom];

  return (
    <div className="cartao p-3">
      <p className="rotulo">{rotulo}</p>
      <p className={cn('tabular mt-1 text-[21px] font-bold leading-none tracking-[-0.02em]', cor)}>
        {valor}
      </p>
      <p className="mt-1 truncate text-[11px] text-textoFraco">{nota}</p>
      {barra !== undefined ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-superficie3">
          <span
            className={cn('block h-full rounded-full', fundoBarra)}
            style={{ width: `${Math.max(2, Math.min(100, barra))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** % com barra: o número sozinho não deixa comparar linhas de relance. */
function BarraPct({ pct }: { pct: number | null }): JSX.Element {
  if (pct === null) return <span className="text-textoSuave">sem medida</span>;
  const cor = pct === 0 ? '#cbd5e1' : pct < 40 ? '#22c55e' : pct < 75 ? '#f59e0b' : '#dc2626';
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-full max-w-[72px] overflow-hidden rounded-full bg-superficie3">
        <span
          className="block h-full rounded-full"
          style={{ width: `${Math.max(2, pct)}%`, background: cor }}
        />
      </span>
      <span className="tabular w-9 shrink-0 text-right text-[12px] font-semibold">{pct}%</span>
    </span>
  );
}
